/**
 * Shared Auth Session & JWT Cookie Manager
 * Scoped under bhutadamarasena.com to enable seamless auth and tier synchronization
 * across the monolithic web application and embedded applets.
 * Aghora Abraham Global LLC
 */

import { encryptData, decryptData } from "./crypto";
import { customHashing, aghoraBitwiseHash } from "./custom-hash";

export interface VedicAuthSession {
  userId: string;
  email: string;
  tier: "PRO" | "LIFETIME_FOUNDER" | "GENERAL";
  activeTerritory?: string;
  subdomainId?: string;
  stripeSessionId?: string;
  issuedAt: number;
  expiresAt: number;
}

export const SESSION_COOKIE_NAME = "vedic_session_jwt";
export const ENCRYPTED_COOKIE_PREFIX = "enc_v1:";

/**
 * Derives the cookie domain for sharing under .bhutadamarasena.com
 */
export function getSharedCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;

  const hostname = window.location.hostname.toLowerCase();
  if (hostname.endsWith("bhutadamarasena.com")) {
    return ".bhutadamarasena.com";
  }
  return undefined; // Localhost or cloud preview fallback
}

/**
 * Sets a cookie scoped under .bhutadamarasena.com
 */
export function setSharedCookie(name: string, value: string, days = 30): void {
  if (typeof document === "undefined") return;

  const domain = getSharedCookieDomain();
  const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString();
  const domainPart = domain ? `; Domain=${domain}` : "";
  const securePart = window.location.protocol === "https:" ? "; Secure" : "";

  document.cookie = `${name}=${encodeURIComponent(value)}${domainPart}; Path=/; Expires=${expires}; SameSite=Lax${securePart}`;
}

/**
 * Gets a cookie value by name
 */
export function getSharedCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const nameEQ = `${name}=`;
  const ca = document.cookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) {
      try {
        return decodeURIComponent(c.substring(nameEQ.length));
      } catch (e) {
        return c.substring(nameEQ.length);
      }
    }
  }

  return null;
}

/**
 * Removes a shared cookie
 */
export function removeSharedCookie(name: string): void {
  if (typeof document === "undefined") return;

  const domain = getSharedCookieDomain();
  const domainPart = domain ? `; Domain=${domain}` : "";
  const securePart = window.location.protocol === "https:" ? "; Secure" : "";

  document.cookie = `${name}=${domainPart}; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${securePart}`;
}

/**
 * Utility to encode session object into a lightweight JWT structure (Base64URL header.payload.signature)
 * using custom hashing signature verification.
 */
export function encodeSessionJWT(session: Partial<VedicAuthSession>): string {
  const header = { alg: "CUSTOM_HASH", typ: "JWT" };
  const now = Math.floor(Date.now() / 1000);
  
  const payload: VedicAuthSession = {
    userId: session.userId || `user_${Math.random().toString(36).substring(2, 10)}`,
    email: session.email || "support@bhutadamarasena.com",
    tier: session.tier || "GENERAL",
    activeTerritory: session.activeTerritory || "",
    subdomainId: session.subdomainId || (typeof window !== "undefined" ? window.location.pathname : "vedic-suite"),
    stripeSessionId: session.stripeSessionId || "",
    issuedAt: session.issuedAt || now,
    expiresAt: session.expiresAt || (now + 30 * 86400),
  };

  const b64Header = btoa(JSON.stringify(header)).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  const b64Payload = btoa(JSON.stringify(payload)).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  
  // Custom cryptographic signature generated via Aghora custom hashing
  const customSig = aghoraBitwiseHash(`${b64Header}.${b64Payload}`, "aghora_abraham_2026");

  return `${b64Header}.${b64Payload}.${customSig}`;
}

/**
 * Decodes and verifies expiration of session JWT
 */
export function decodeSessionJWT(jwtString: string): VedicAuthSession | null {
  try {
    if (!jwtString) return null;

    let token = jwtString;
    if (token.startsWith(ENCRYPTED_COOKIE_PREFIX)) {
      token = token.substring(ENCRYPTED_COOKIE_PREFIX.length);
    }

    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const b64Payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonStr = atob(b64Payload);
    const session = JSON.parse(jsonStr) as VedicAuthSession;

    const nowSeconds = Math.floor(Date.now() / 1000);
    if (session.expiresAt && session.expiresAt < nowSeconds) {
      console.warn("[AUTH JWT EXPIRED] Session token expired.");
      return null;
    }

    return session;
  } catch (err) {
    console.error("Failed to decode session JWT:", err);
    return null;
  }
}

/**
 * Saves active user session to secure encrypted cookie under .bhutadamarasena.com
 * Completely replaces storing unencrypted session tokens in localStorage.
 */
export async function saveAuthSessionAsync(sessionData: Partial<VedicAuthSession>): Promise<VedicAuthSession> {
  const current = getAuthSession();

  const updated: VedicAuthSession = {
    userId: sessionData.userId || current?.userId || `usr_${Date.now()}`,
    email: sessionData.email || current?.email || "support@bhutadamarasena.com",
    tier: sessionData.tier || current?.tier || "GENERAL",
    activeTerritory: sessionData.activeTerritory !== undefined ? sessionData.activeTerritory : current?.activeTerritory,
    subdomainId: sessionData.subdomainId || current?.subdomainId || (typeof window !== "undefined" ? window.location.pathname : "vedic-suite"),
    stripeSessionId: sessionData.stripeSessionId || current?.stripeSessionId || "",
    issuedAt: Math.floor(Date.now() / 1000),
    expiresAt: Math.floor(Date.now() / 1000) + 30 * 86400,
  };

  const rawJwt = encodeSessionJWT(updated);

  let finalCookieValue = rawJwt;
  try {
    const encrypted = await encryptData(rawJwt);
    if (encrypted) {
      finalCookieValue = `${ENCRYPTED_COOKIE_PREFIX}${encrypted}`;
    }
  } catch (e) {
    console.warn("AES encryption fallback for cookie", e);
  }

  // Set shared cookie across .bhutadamarasena.com
  setSharedCookie(SESSION_COOKIE_NAME, finalCookieValue, 30);

  // Clear unencrypted session tokens from localStorage to enforce cookie-first security
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(SESSION_COOKIE_NAME);
      localStorage.removeItem("vedic_pro_tier_active");
    } catch (e) {}
  }

  // Dispatch custom browser event for reactive UI re-render
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("vedic_session_changed", { detail: updated }));
  }

  return updated;
}

/**
 * Synchronous session saver (Fallback / Immediate DOM setter)
 */
export function saveAuthSession(sessionData: Partial<VedicAuthSession>): VedicAuthSession {
  const current = getAuthSession();

  const updated: VedicAuthSession = {
    userId: sessionData.userId || current?.userId || `usr_${Date.now()}`,
    email: sessionData.email || current?.email || "support@bhutadamarasena.com",
    tier: sessionData.tier || current?.tier || "GENERAL",
    activeTerritory: sessionData.activeTerritory !== undefined ? sessionData.activeTerritory : current?.activeTerritory,
    subdomainId: sessionData.subdomainId || current?.subdomainId || (typeof window !== "undefined" ? window.location.hostname : "global"),
    stripeSessionId: sessionData.stripeSessionId || current?.stripeSessionId || "",
    issuedAt: Math.floor(Date.now() / 1000),
    expiresAt: Math.floor(Date.now() / 1000) + 30 * 86400,
  };

  const token = encodeSessionJWT(updated);
  setSharedCookie(SESSION_COOKIE_NAME, token, 30);

  // Clear any unencrypted tokens from localStorage
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(SESSION_COOKIE_NAME);
      localStorage.removeItem("vedic_pro_tier_active");
    } catch (e) {}
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("vedic_session_changed", { detail: updated }));
  }

  return updated;
}

/**
 * Retrieves current active authentication session from shared domain cookie (.bhutadamarasena.com)
 */
export function getAuthSession(): VedicAuthSession | null {
  const cookieJwt = getSharedCookie(SESSION_COOKIE_NAME);
  if (!cookieJwt) return null;

  return decodeSessionJWT(cookieJwt);
}

// In-memory cooldown cache to prevent network request spamming
let lastBetterAuthFetch = 0;
let lastBetterAuthResult: VedicAuthSession | null = null;
const BETTER_AUTH_COOLDOWN_MS = 30000;

/**
 * Queries Better Auth session from the monolithic website (bhutadamarasena.com/api/auth/get-session)
 */
export async function fetchBetterAuthSession(): Promise<VedicAuthSession | null> {
  if (typeof window === "undefined") return null;

  const now = Date.now();
  if (now - lastBetterAuthFetch < BETTER_AUTH_COOLDOWN_MS) {
    return lastBetterAuthResult;
  }
  lastBetterAuthFetch = now;

  try {
    const authEndpoint = window.location.hostname.includes("bhutadamarasena.com")
      ? "/api/auth/get-session"
      : "https://bhutadamarasena.com/api/auth/get-session";

    const res = await fetch(authEndpoint, {
      method: "GET",
      credentials: "include",
      headers: { "Accept": "application/json" }
    });

    if (res.ok) {
      const data = (await res.json()) as any;
      if (data?.session && data?.user) {
        const tier: "PRO" | "LIFETIME_FOUNDER" | "GENERAL" = 
          data.user.tier === "PRO" || data.user.tier === "LIFETIME_FOUNDER" 
            ? data.user.tier 
            : (data.user.role === "admin" || data.user.isPro ? "PRO" : "GENERAL");
        
        const session: VedicAuthSession = {
          userId: data.user.id || data.session.userId,
          email: data.user.email || "user@bhutadamarasena.com",
          tier,
          issuedAt: Math.floor(Date.now() / 1000),
          expiresAt: data.session.expiresAt 
            ? Math.floor(new Date(data.session.expiresAt).getTime() / 1000) 
            : (Math.floor(Date.now() / 1000) + 30 * 86400)
        };
        saveAuthSession(session);
        lastBetterAuthResult = session;
        return session;
      }
    }
  } catch (err) {
    // Monolith auth service unreachable or running local sandbox; fallback to shared cookie
  }
  lastBetterAuthResult = null;
  return null;
}

/**
 * Async session retriever supporting Better Auth and encrypted cookie payloads
 */
export async function getAuthSessionAsync(): Promise<VedicAuthSession | null> {
  // 1. First attempt to check Better Auth session from the monolith website
  const betterAuthSession = await fetchBetterAuthSession();
  if (betterAuthSession) {
    return betterAuthSession;
  }

  // 2. Fall back to shared domain cookie (.bhutadamarasena.com)
  const cookieVal = getSharedCookie(SESSION_COOKIE_NAME);
  if (!cookieVal) return null;

  if (cookieVal.startsWith(ENCRYPTED_COOKIE_PREFIX)) {
    const cipherText = cookieVal.substring(ENCRYPTED_COOKIE_PREFIX.length);
    try {
      const decryptedJwt = await decryptData(cipherText);
      if (decryptedJwt) {
        return decodeSessionJWT(decryptedJwt);
      }
    } catch (e) {
      console.warn("Decryption of session cookie failed", e);
    }
  }

  return decodeSessionJWT(cookieVal);
}

/**
 * Clears authentication session across all subdomains
 */
export function clearAuthSession(): void {
  removeSharedCookie(SESSION_COOKIE_NAME);
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(SESSION_COOKIE_NAME);
      localStorage.removeItem("vedic_pro_tier_active");
    } catch (e) {}
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("vedic_session_changed", { detail: null }));
  }
}
